import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Zap } from "lucide-react";

export function AdminTestBandwidth({ onDone }) {
  const [status, setStatus] = useState(null);
  const [amount, setAmount] = useState(1);
  const [busy, setBusy] = useState(false);
  const loadStatus = useCallback(async () => {
    try { const { data } = await api.get("/admin/test-allocation-status"); setStatus(data); } catch (e) { logError(e); }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  const canAllocate = status?.can_allocate;
  const max = status?.max_gb ?? 10;
  const allocate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/allocate-test-bandwidth", { amount });
      toast.success(`Allocated ${data.amount} GB to ${data.account_username}`);
      await loadStatus();
      onDone && onDone();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Allocation failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="led-border bg-white border border-[#E4EEEC] rounded-3xl p-6 shadow-sm" data-testid="admin-test-bandwidth-card">
      <div className="flex items-center gap-2 mb-1">
        <Zap className="w-5 h-5 text-neon" aria-hidden="true" />
        <h3 className="font-head text-lg font-semibold text-ink">Test Bandwidth</h3>
        <span className="text-[10px] font-mono tracking-wider text-neon bg-[#E7F7F4] rounded-full px-2 py-0.5">ADMIN</span>
      </div>
      <p className="text-zinc-500 text-sm mb-4">Allocate up to {max} GB to your own account for testing — once per day, no checkout required.</p>
      <div className="flex flex-wrap items-center gap-3">
        <Input type="number" min={1} max={max} value={amount} onChange={(e) => setAmount(Math.max(1, Math.min(max, Number(e.target.value) || 1)))} disabled={!canAllocate} className="w-24 h-11 bg-white border-[#E4EEEC] focus:border-neon rounded-xl font-mono text-ink" data-testid="admin-alloc-amount-input" />
        <span className="text-zinc-500 text-sm font-mono">GB</span>
        <Button onClick={allocate} disabled={busy || !canAllocate} className="bg-neon text-white hover:bg-neon-hover font-semibold rounded-xl disabled:opacity-60" data-testid="admin-allocate-button">{busy ? "Allocating…" : `Allocate ${amount} GB`}</Button>
      </div>
      {status && !canAllocate && (
        <p className="text-xs text-zinc-400 mt-3" data-testid="admin-alloc-cooldown">Already used today · available again {status.next_at ? new Date(status.next_at).toLocaleString() : "tomorrow"}.</p>
      )}
    </div>
  );
}
